import { useEffect, useState } from "react";
import { Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, errorText } from "@/lib/api";
import { WhatsAppIcon } from "@/components/site/Icons";
import { STATUS, StatusBadge, fmtDate, fmtEventDate } from "./RequestsTab";

const Field = ({ label, children }) => (
  <div><p className="text-xs uppercase tracking-wider text-ink/50">{label}</p><div className="mt-1 text-sm">{children}</div></div>
);

const Action = ({ href, icon, label, testId, external }) => (
  <a href={href} {...(external && { target: "_blank", rel: "noopener noreferrer" })} className="flex-1 flex flex-col items-center gap-1 rounded-xl border py-3 text-xs hover:border-granate hover:text-granate transition-colors" data-testid={testId}>
    {icon}{label}
  </a>
);

export const RequestDetail = ({ id, onClose, onChanged }) => {
  const [a, setA] = useState(null);
  useEffect(() => {
    setA(null);
    if (id) api.get(`/admin/appointments/${id}`).then((r) => setA(r.data)).catch((e) => toast.error(errorText(e)));
  }, [id]);

  const setStatus = async (status) => {
    try {
      const { data } = await api.patch(`/admin/appointments/${id}`, { status });
      setA(data); onChanged(); toast.success(`Estado: ${STATUS[status].label}`);
    } catch (e) { toast.error(errorText(e)); }
  };

  const digits = a?.phone.replace(/[^\d]/g, "") || "";
  const wa = digits.length === 9 ? `34${digits}` : digits;

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-white w-full sm:max-w-md overflow-y-auto" data-testid="request-detail">
        {a && (
          <>
            <SheetHeader><SheetTitle className="font-display text-3xl font-normal text-left">{a.name}</SheetTitle></SheetHeader>
            <div className="mt-2"><StatusBadge status={a.status} /></div>
            <div className="mt-6 flex gap-2">
              <Action href={`tel:${a.phone}`} icon={<Phone className="w-5 h-5" />} label="Llamar" testId="request-call-link" />
              <Action href={`https://wa.me/${wa}`} icon={<WhatsAppIcon className="w-5 h-5" />} label="WhatsApp" testId="request-whatsapp-link" external />
              <Action href={`mailto:${a.email}?subject=${encodeURIComponent("Tu cita con La Patrona")}`} icon={<Mail className="w-5 h-5" />} label="Email" testId="request-email-link" />
            </div>
            <div className="mt-8 grid grid-cols-2 gap-5">
              <Field label="Email"><span className="break-all">{a.email}</span></Field>
              <Field label="Teléfono">{a.phone}</Field>
              <Field label="Servicio">{a.service}</Field>
              <Field label="Fecha del evento">{fmtEventDate(a.event_date)}</Field>
              <Field label="Invitados">{a.guests ?? "—"}</Field>
              <Field label="Recibida">{fmtDate(a.created_at)}</Field>
            </div>
            <div className="mt-6"><Field label="Su idea"><p className="whitespace-pre-wrap text-ink/80" data-testid="request-detail-message">{a.message || "—"}</p></Field></div>
            <div className="mt-8">
              <p className="text-xs uppercase tracking-wider text-ink/50 mb-2">Cambiar estado</p>
              <div className="grid grid-cols-3 gap-2" data-testid="request-status-buttons">
                {Object.entries(STATUS).map(([k, v]) => (
                  <button key={k} onClick={() => setStatus(k)} className={`rounded-lg py-2 text-sm border transition-colors ${a.status === k ? "bg-ink text-white border-ink" : "hover:border-ink/40"}`} data-testid={`request-status-${k}`}>{v.label}</button>
                ))}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
